import i18n from '@atpoint/atpjs/helpers/i18n'
import ErrorBoundary from '@forms/components/error_boundary'
import FileUpload from '@forms/components/file_upload'
import { InputAddress, InputGender, InputNames } from '@forms/components/form'
import { Honeypot } from '@forms/components/honeypot'
import { Map as ImmutableMap, type List } from 'immutable'
import { Component, type FormEvent } from 'react'
import { ButtonsWrapper, StyledButton } from 'swisscard/components/buttons'
import {
  InputBoolean,
  InputCheckboxes,
  InputDate,
  InputEmail,
  InputIban,
  InputMoney,
  InputSelect,
  InputSwitch,
  InputTel,
  InputText,
  InputTextarea,
  InputTime,
} from 'swisscard/components/form'
import { BusinessPlaceInput } from 'swisscard/components/form/business_place_input/business_place_input'
import { H2, P } from 'swisscard/components/typography'

type LabelType = {
  label: string
}

export type ElementType = {
  id: string
  title: string
  type: string
  required?: boolean
  placeholder?: string | null
  infoBottom?: string | null
  options?: Array<string>
  value?: string | unknown | null
}

const TitleElement = (props: LabelType) => <H2>{props.label}</H2>
const ParagraphElement = (props: LabelType) => <P variant="info">{props.label}</P>

const inputByType = Object.freeze({
  string_input: InputText,
  text_input: InputTextarea,
  date_input: InputDate,
  time_input: InputTime,
  email_input: InputEmail,
  phone_input: InputTel,
  checkboxes_input: InputCheckboxes,
  select_input: InputSelect,
  radios_input: InputSwitch, // was radios_input: Radio
  title_element: TitleElement,
  paragraph_element: ParagraphElement,
  acceptance_input: InputBoolean,
  file_input: FileUpload,
  currency_input: InputMoney,
  country_select_input: InputSelect,
  iban_input: InputIban,
  address_input: InputAddress,
  names_input: InputNames,
  gender_input: InputGender,
  business_place_input: BusinessPlaceInput,
})

const defaultPropertiesByType = (type: string, element: ImmutableMap<string, any>) => {
  const value = element.get('value')

  const defaultProps = {
    label: element.get('title') as string,
    value,
  }

  return (
    // @ts-ignore
    {
      currency_input: {
        ...defaultProps,
      },
      time_input: {
        ...defaultProps,
        placeholder: i18n.trans(['format', 'time']),
        pattern: '^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$',
      },
      date_input: {
        ...defaultProps,
        placeholder: i18n.trans(['format', 'date']),
      },
      checkboxes_input: {
        ...defaultProps,
        multiple: true,
        values: valuesFromOptions(element.get('options')),
      },
      acceptance_input: {
        values: [{ label: element.get('title'), value: 1 }],
        value,
        // biome-ignore lint/security/noDangerouslySetInnerHtml: allowing behaviour for this use case.
        children: <div dangerouslySetInnerHTML={{ __html: element.get('title') }} />,
      },
      radios_input: {
        ...defaultProps,
        values: valuesFromOptions(element.get('options')),
      },
      select_input: {
        ...defaultProps,
        has_empty: true,
        values: valuesFromOptions(element.get('options')),
      },
      country_select_input: {
        ...defaultProps,
        has_empty: true,
        values: valuesFromOptionsCountry(element.get('options')),
      },
      file_input: {
        value: value,
      },
      address_input: {
        ...defaultProps,
        value: value || ImmutableMap(),
      },
      business_place_input: {
        ...defaultProps,
      },
    }[type] || defaultProps
  )
}

const valuesFromOptions = (options: List<string> | null): Array<{ value: string; label: string }> => {
  const output = options?.toJS() as Array<string>
  return output?.map((option: string) => ({ value: option, label: option }))
}

const valuesFromOptionsCountry = (options?: List<List<string>>): Array<{ value: string; label: string }> => {
  const output = options?.toJS() as Array<Array<string>>
  return output?.map((option: Array<string>) => ({ value: option[0], label: option[1] }))
}

type FormProps = {
  name: string
  elements: List<ImmutableMap<string, any>>
  submit_button_text?: string
  onSubmit: (event: FormEvent) => void
  updater: (value: any) => void
}

class Form extends Component<FormProps> {
  render() {
    const { elements, updater, onSubmit, submit_button_text } = this.props
    const submitButtonText = submit_button_text || i18n.trans(['button', 'send'])

    return (
      <>
        {elements.map((element, index) => {
          const focused = index === 0
          const { type, ...elementProperties } = element.toJS() as ElementType
          const defaultProperties = defaultPropertiesByType(type, element)
          const properties = { ...elementProperties, ...defaultProperties }
          // @ts-ignore
          const ElementByType = inputByType[type]
          if (ElementByType === undefined) {
            return null
          }

          return (
            <ErrorBoundary key={index}>
              {ElementByType && (
                <ElementByType
                  {...properties}
                  errorMessage={element.get('errorMessage')}
                  focused={focused}
                  onChange={updater.bind(this, [index, 'value'])}
                />
              )}
              <br />
            </ErrorBoundary>
          )
        })}
        <Honeypot />
        <ButtonsWrapper>
          <StyledButton primary onClick={onSubmit}>
            {submitButtonText}
          </StyledButton>
        </ButtonsWrapper>
      </>
    )
  }
}

export default Form
